import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import CaseDetailsForm, {
    type CreatedPortalCase,
} from "../../uploader/caseDetailsFormNew";
import FileUploader, {
    type LibraryUploadItem,
} from "../../uploader/fileUploader";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { fetchCaseById } from "../../../store/user/userSlice";
import { fetchBoxAccessToken } from "../../../store/common/authSlice";
import { fetchCaseFilesByCaseId } from "../../../store/common/fetchBoxFileSlice";
import { casesPortalCaseHref } from "../../../utils/linkedPortalCase";
import {
    isAfterDesignItem,
    type HistoryItem,
} from "../../../types/smileDesign/historyItem";

const SERVICE_LABELS: Record<string, string> = {
    crownAndBridge: "Crown & Bridge",
    Implant: "Implant",
    smileDesign: "Smile Design / Mock-up",
    veneers: "No-prep Veneers",
    "complete-denture": "LDP Complete denture",
    "partial-denture": "LDP Partial denture",
    "partial-cast": "RPD Framework",
    surgicalGuide: "Surgical Guide",
    "nightguard-bite-splint": "Nightguard / Bite splint",
    "digital-model": "Digital model",
    otherTreatments: "Other treatments",
    filledOrder: "Upload filled order",
};

const formatServiceType = (serviceType?: string | string[] | null) => {
    let keys: string[] = [];
    if (Array.isArray(serviceType)) keys = serviceType;
    else if (typeof serviceType === "string" && serviceType.trim()) {
        keys = serviceType.split(",").map((s) => s.trim()).filter(Boolean);
    }
    if (!keys.length) return "—";
    return keys.map((key) => SERVICE_LABELS[key] || key).join(", ");
};

const formatStatusLabel = (status?: string | null) => {
    if (!status) return "—";
    if (status.toLowerCase() === "query pending") return "Query Pending";
    if (status.toLowerCase() === "under process") return "Under Process";
    return status;
};

function labTokenFromSmileId(smileId?: string | null) {
    if (!smileId) return "";
    const marker = smileId.toUpperCase().indexOf("-SD-");
    if (marker <= 0) return "";
    return smileId.slice(0, marker).replace(/[^A-Za-z]/g, "").toUpperCase();
}

type Props = {
    smileId?: string | null;
    patientName: string;
    beforePreview: string | null;
    beforeFile: File | null;
    history: HistoryItem[];
    liveEditedAfterUrl?: string | null;
    createdCase: CreatedPortalCase | null;
    linkedCaseId?: string | null;
    alreadyLinked?: boolean;
    isSmileCaseLoading?: boolean;
    onCaseCreated: (created: CreatedPortalCase) => void;
};

function buildLibrary(
    history: HistoryItem[],
    beforePreview: string | null,
    beforeFile: File | null,
    patientName: string,
    liveEditedAfterUrl?: string | null
): LibraryUploadItem[] {
    const items: LibraryUploadItem[] = [];

    if (beforePreview || beforeFile) {
        items.push({
            id: "before",
            label: "Before photo",
            group: "Patient photo",
            previewUrl: beforePreview || "",
            fileName: beforeFile?.name || `${patientName || "Patient"}-before.jpg`,
            file: beforeFile || undefined,
        });
    }

    history.filter(isAfterDesignItem).forEach((item, index) => {
        const style = item.smileType || "Generated smile";
        items.push({
            id: `after-${index}`,
            label: style,
            group: "Generated smiles",
            previewUrl: item.image,
            fileName: item.imageName,
        });
        if (item.overlay?.image) {
            items.push({
                id: `edited-${index}`,
                label: `${style} · edited after`,
                group: "Edited after",
                previewUrl: item.overlay.image,
                fileName: item.overlay.imageName,
            });
        }
        if (item.restored?.image) {
            items.push({
                id: `refine-${index}`,
                label: `${style} · refine`,
                group: "Refine",
                previewUrl: item.restored.image,
                fileName: item.restored.imageName,
            });
        }
        if (item.wireframe?.image) {
            items.push({
                id: `wireframe-${index}`,
                label: `${style} · wireframe`,
                group: "Wireframe",
                previewUrl: item.wireframe.image,
                fileName: item.wireframe.imageName,
            });
        }
    });

    if (
        liveEditedAfterUrl &&
        !items.some((item) => item.previewUrl === liveEditedAfterUrl)
    ) {
        items.push({
            id: "live-edited-after",
            label: "Edited after",
            group: "Edited after",
            previewUrl: liveEditedAfterUrl,
            fileName: `${patientName || "Patient"}-edited-after.png`,
        });
    }

    return items.filter((item) => item.previewUrl || item.file);
}

export default function SmileCaseOrderSection({
    smileId,
    patientName,
    beforePreview,
    beforeFile,
    history,
    liveEditedAfterUrl = null,
    createdCase,
    linkedCaseId = null,
    alreadyLinked = false,
    isSmileCaseLoading = false,
    onCaseCreated,
}: Props) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { user, boxAccessToken } = useAppSelector((state) => state.auth);
    const { caseFiles, fetching } = useAppSelector((state) => state.fileUpload);
    const { selectedCase } = useAppSelector((state) => state.userCases);

    const libraryItems = useMemo(
        () =>
            buildLibrary(
                history,
                beforePreview,
                beforeFile,
                patientName,
                liveEditedAfterUrl
            ),
        [history, beforePreview, beforeFile, patientName, liveEditedAfterUrl]
    );

    const caseId = createdCase?.case_id || linkedCaseId || "";
    const linkedPortalCase =
        selectedCase?.case_id === caseId ? selectedCase : null;
    const folderFromCase = linkedPortalCase?.box_folder_id || null;
    const boxFolderId = createdCase?.box_folder_id || folderFromCase || null;
    const caseOwner =
        linkedPortalCase?.case_owner ||
        createdCase?.case_owner ||
        patientName ||
        "—";
    const casesHref = caseId
        ? casesPortalCaseHref(caseId, user?.role)
        : "";

    useEffect(() => {
        if (!caseId) return;
        if (!boxAccessToken) {
            dispatch(fetchBoxAccessToken());
        }
        if (!linkedPortalCase) {
            dispatch(fetchCaseById(caseId));
        }
    }, [caseId, boxAccessToken, linkedPortalCase, dispatch]);

    useEffect(() => {
        if (!caseId || !boxAccessToken || !boxFolderId) return;
        dispatch(fetchCaseFilesByCaseId({ case_id: caseId }));
    }, [caseId, boxAccessToken, boxFolderId, dispatch]);

    const clientFiles =
        caseFiles?.filter(
            (file) => file.file_type?.toLowerCase() === "client_upload"
        ) ?? [];
    const hasClientUploads = clientFiles.length > 0;

    const showCreateForm = !caseId;
    const canFinishWithoutNewUpload = alreadyLinked || hasClientUploads;

    if (isSmileCaseLoading && showCreateForm) {
        return (
            <section className="mt-6">
                <p className="text-sm text-[#9aa3b2]">Loading case details...</p>
            </section>
        );
    }

    return (
        <section className="mt-6 space-y-4">
            {showCreateForm ? (
                <>
                <div>
                    <h2 className="text-lg font-semibold text-white">Create case</h2>
                    <p className="mt-1 text-sm text-[#9aa3b2]">
                        Smile Design is already selected. The patient name comes from this smile and can be edited before you submit.
                    </p>
                </div>
                <CaseDetailsForm
                    embedded
                    forceTheme="dark"
                    lockedServiceKey="smileDesign"
                    initialPatientName={patientName}
                    initialUploadToken={labTokenFromSmileId(smileId)}
                    smileId={smileId || ""}
                    onCaseCreated={onCaseCreated}
                />
                </>
            ) : (
                <div className="rounded-2xl border border-white/10 bg-[#1a1f27] p-4 sm:p-6 text-[#e8eaed]">
                    <p className="text-xs text-[#6b7380]">
                        {alreadyLinked ? "Connected cases portal case" : "Step 2 of 2"}
                    </p>
                    <h2 className="mt-0.5 text-xl font-semibold text-white">
                        {alreadyLinked
                            ? `Case ${caseId}`
                            : `Upload case files for ${caseId}`}
                    </h2>
                    <p className="mt-2 text-sm text-[#9aa3b2]">
                        {alreadyLinked
                            ? "This smile is already linked. Upload more files if needed."
                            : `Case created for ${caseOwner}. Select the smile images to attach, and upload any other files this case needs.`}
                    </p>

                    <dl className="mt-4 grid gap-3 sm:grid-cols-2 text-sm">
                        <div>
                            <dt className="text-[#6b7380]">Case ID</dt>
                            <dd className="mt-0.5">
                                {casesHref ? (
                                    <a
                                        href={casesHref}
                                        className="font-semibold text-[#1DA8C6] hover:underline break-all"
                                    >
                                        {caseId}
                                    </a>
                                ) : (
                                    <span className="font-semibold text-white break-all">
                                        {caseId}
                                    </span>
                                )}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-[#6b7380]">Patient/Job</dt>
                            <dd className="mt-0.5 text-white">{caseOwner}</dd>
                        </div>
                        <div>
                            <dt className="text-[#6b7380]">Status</dt>
                            <dd className="mt-0.5 text-white">
                                {formatStatusLabel(linkedPortalCase?.status)}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-[#6b7380]">Service</dt>
                            <dd className="mt-0.5 text-white">
                                {formatServiceType(linkedPortalCase?.service_type)}
                            </dd>
                        </div>
                    </dl>

                    <p className="mt-3 text-sm text-[#9aa3b2]">
                        In case of errors, refresh this page or reach out to{" "}
                        <a
                            href="mailto:admin@toothsketch.com"
                            className="text-[#2fa9bf]"
                        >
                            admin@toothsketch.com
                        </a>
                    </p>

                    <div className="mt-4 text-sm" aria-busy={fetching}>
                        <p className="mb-2 font-medium text-white">Uploaded files</p>
                        {fetching ? (
                            <p className="text-[#9aa3b2]">Loading files...</p>
                        ) : hasClientUploads ? (
                            <ul className="space-y-1 text-[#e8eaed]">
                                {clientFiles.map((file, index) => (
                                    <li key={file.fileId || index}>{file.file_name}</li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-[#9aa3b2]">No files uploaded yet.</p>
                        )}
                    </div>

                    <div className="mt-4">
                        <p className="mb-2 text-sm font-medium text-white">
                            {alreadyLinked ? "Upload more files" : "Upload files"}
                        </p>
                        <FileUploader
                            variant="dark"
                            userId={user?.user_id || 0}
                            caseId={caseId}
                            file_type="CLIENT_UPLOAD"
                            caseFolderId={boxFolderId ? String(boxFolderId) : null}
                            accessToken={boxAccessToken || ""}
                            caseOwner={caseOwner}
                            libraryItems={libraryItems}
                            disabled={!boxFolderId || !boxAccessToken}
                            disabledReason={
                                !boxAccessToken || !boxFolderId
                                    ? "Preparing the case folder. This usually takes a moment."
                                    : undefined
                            }
                        />
                    </div>

                    <div className="mt-4 flex flex-col items-end gap-2">
                        {!alreadyLinked && !hasClientUploads && !fetching ? (
                            <p className="text-sm text-amber-300">
                                Upload at least one file before completing the case.
                            </p>
                        ) : null}
                        <button
                            type="button"
                            className="rounded-xl bg-[#1DA8C6] px-4 py-2 text-sm font-semibold text-[#08151A] disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={!canFinishWithoutNewUpload}
                            onClick={() =>
                                navigate(
                                    casesHref ||
                                        (user?.role?.toLowerCase() === "user"
                                            ? "/dashboard/my-cases"
                                            : "/dashboard/admin/cases")
                                )
                            }
                        >
                            {alreadyLinked ? "Open case" : "Complete Case"}
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
}
